import { useEffect, useRef } from 'react';
import { UrlStateManager } from '@/utils/UrlStateManager';
import { UrlParamConfig } from '@/scripts/types/urlSync';

/**
 * Creates a URL synchronization hook for the given parameters
 * @param urlStateManager The URL state manager instance
 * @param configs Array of parameter configurations
 */
export const createUrlSync = <T extends unknown[]>(
  urlStateManager: UrlStateManager,
  ...configs: { [K in keyof T]: UrlParamConfig<T[K]> }
) => {
  return () => {
    // Keep track of whether we're updating from URL to prevent loops
    // Start as true to suppress the initial write pass until hydration completes
    const isUpdatingFromUrl = useRef(true);

    // Initialize state from URL on mount with a small delay
    useEffect(() => {
      const initTimeout = setTimeout(() => {
        const params = urlStateManager.getAllParams();
        isUpdatingFromUrl.current = true;

        try {
          configs.forEach(config => {
            const urlValue = params[config.paramKey];
            if (urlValue) {
              try {
                const parsedValue = config.strategy.deserialize(urlValue);
                const currentValue = config.getValue();

                // Only update if values are different
                if (JSON.stringify(currentValue) !== JSON.stringify(parsedValue)) {
                  config.setValue(parsedValue);
                }
              } catch {
                /* noop */
              }
            }
          });
        } finally {
          // Ensure we reset the flag even if there's an error
          isUpdatingFromUrl.current = false;
        }
      }, 50); // Small delay to ensure DOM is ready

      return () => clearTimeout(initTimeout);
    }, []); // Only run on mount

    // Update URL when state changes
    useEffect(
      () => {
        // Don't update URL if we're currently updating from URL
        if (isUpdatingFromUrl.current) return;

        // Batch URL updates to prevent multiple history entries
        const updates = configs.reduce<Record<string, string>>((acc, config) => {
          const value = config.getValue();

          if (config.strategy.shouldUpdate(value)) {
            acc[config.paramKey] = config.strategy.serialize(value);
          }
          return acc;
        }, {});

        // Get current params and compare with updates
        const currentParams = urlStateManager.getAllParams();
        let hasChanges = false;

        // Remove params that should no longer be present
        Object.keys(currentParams).forEach(key => {
          if (configs.some(c => c.paramKey === key) && !(key in updates)) {
            urlStateManager.removeParam(key);
            hasChanges = true;
          }
        });

        // Add or update changed params
        Object.entries(updates).forEach(([key, value]) => {
          if (currentParams[key] !== value) {
            urlStateManager.addOrUpdateParam(key, value);
            hasChanges = true;
          }
        });

        // Only trigger URL update if there were actual changes
        if (hasChanges) {
          urlStateManager.updateUrl();
        }
      },
      // Use a more stable dependency array - track the actual values
      configs.map(config => {
        try {
          return JSON.stringify(config.getValue());
        } catch {
          return '';
        }
      })
    ); // Dependencies are the serialized current values
  };
};
